import * as moment from "moment";
import VerbConstants from "motw_core/Constants/VerbConstants";
import IDream from "motw_core/Entity/IDream";
import { dreamService } from "motw_core/Services/DreamService";
import * as React from "react";
import { SelectDreamAttribute } from "source/app/presentation/MainPanel/Dreams/DreamEditor/Attributes/SelectDreamAttribute";
import Render from "source/app/presentation/Shared/Render";
import { Style } from "source/app/presentation/Shared/Styles/CSS";
import Nameof from "source/app/services/NameOf";
import { State } from "source/app/services/UI/State/State";
import * as toastr from "toastr";
import { resourceNameDream } from "../../../../../../../motw_core/Entity/IDream";
import Data from "../../../../services/Data";
import { ESharedComponent } from "../../../../services/UI/State/ESharedComponent";
import { FormSelect } from "../../../Shared/Form/Select/FormSelect";
import { FormTextArea } from "../../../Shared/Form/TextArea/FormTextArea";
import { GetDreamAttributePrefix } from "../DreamAttributeIcon/AttributeMap/AttributeMap";
import DreamHelpers, {
    difficultyRange,
    DreamEmotionSymbol,
} from "../DreamHelpers/DreamHelpers";
import { DreamHasLinks } from "../DreamRow/DreamRow";
import {
    ConvertDifficultyToDreamTime,
    IDreamTime,
} from "../DreamTimes/DreamTimes";
import DreamToolbar, {
    DreamToolbarCalledFrom,
} from "../DreamToolbar/DreamToolbar";
import { Switch } from "../DreamTools/Switch/Switch";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import { IDreamsState } from "../IDreamsState";
import * as styles from "./DreamEditor.css";
import { DreamType } from "./DreamType";
import {
    dreamEditorChevronPanelId,
    DreamEditorHashTags,
} from "./HashTags/DreamEditorHashTags";
import { GetShortenedDreamName } from "./Helpers/GetShortenedDreamName";
import { GetEditedDream } from "./Helpers/GetEditedDream";
import { ESharedFunctionMultiple } from "motw_core/Enums/ESharedFunction";
import { SetStateMultiple } from "source/app/services/UI/State/SetState";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import IconWithText from "source/app/services/Shared/IconWithText/IconWithText";

interface IDreamEditorProps {
    dreamType: DreamType;
    dream: IDream;
    allDreams: IDream[];
    initializedDreamProperties?: Partial<IDream>;
}

export interface IDreamEditorState {
    dream: IDream;
}

export const emptyDream: IDream = {
    id: 0,
    name: "",
    difficulty: 5,
    attributes: [],
    linkid: null,
    emotion: "+",
    lastmodifiedtime: moment().format(),
    notes: "",
    isresonating: false,
};

export class DreamEditor extends React.Component<
    IDreamEditorProps,
    IDreamEditorState
> {
    constructor(props: IDreamEditorProps) {
        super(props);

        this.state = {
            dream: { ...emptyDream, ...this.props.initializedDreamProperties },
        };
    }

    componentDidMount() {
        if (this.props.dreamType !== DreamType.New) {
            this.setState({ dream: this.props.dream });
        } else {
            document.getElementById("inputDreamName")?.focus();
        }

        State.ShareClass(ESharedComponent.DreamEditor, this);
    }

    componentDidUpdate() {
        State.ShareClass(ESharedComponent.DreamEditor, this);
    }

    HandleValueChange(newValue: any, property: string) {
        if (property === `difficulty`) {
            newValue = parseInt(newValue);
        }
        this.setState({
            dream: {
                ...this.state.dream,
                [property]: newValue,
            },
        });
    }

    render() {
        const thisDream = this.state.dream;
        const allDreams = this.props.allDreams;
        const allLinks = DreamHelpers.GetAllLinks(thisDream, allDreams);
        const isLinked = DreamHasLinks(thisDream, allDreams);

        return (
            <div>
                <DreamToolbar
                    row={thisDream}
                    allDreams={allDreams}
                    allLinks={allLinks}
                    calledFrom={DreamToolbarCalledFrom.DreamEditor}
                />
                <div>
                    <FormTextArea
                        id="inputDreamName"
                        label={GetDreamAttributePrefix(thisDream.attributes)}
                        width="84%"
                        value={thisDream.name}
                        onChange={(value) => {
                            const showHashTags = value.includes(`#`);

                            if (showHashTags) {
                                SetStateMultiple(
                                    ESharedFunctionMultiple.ChevronPanel_isOpen,
                                    dreamEditorChevronPanelId,
                                    true
                                );
                            }

                            value = value.replaceAll(`# `, ``);
                            this.HandleValueChange(value, "name");
                        }}
                        onClear={() => this.HandleValueChange("", "name")}
                    />
                </div>
                <RemovePatchTextLink
                    dreamName={thisDream.name}
                    onRemovePatchText={(newDreamName) =>
                        this.HandleValueChange(newDreamName, "name")
                    }
                />
                <DreamEditorHashTags DreamEditor={this} />
                <div>
                    <FormSelect
                        id="selectDifficulty"
                        label="Difficulty"
                        width="65px"
                        suggestions={difficultyRange.map((elem) =>
                            elem.toString()
                        )}
                        onChange={(event) =>
                            this.HandleValueChange(
                                event.target.value,
                                "difficulty"
                            )
                        }
                        value={thisDream.difficulty}
                    />
                    <Render id="dreamTimeText" when={thisDream.difficulty}>
                        <DreamTimeText
                            dreamTime={ConvertDifficultyToDreamTime(
                                thisDream.difficulty
                            )}
                        />
                    </Render>
                </div>
                <div>
                    <FormSelect
                        id="selectEmotion"
                        label="Emotion"
                        width="100%"
                        classes={styles.emotionSelector}
                        suggestions={VerbConstants.Verbs.All().map(
                            (elem) => elem.symbol
                        )}
                        onChange={(event) =>
                            this.HandleValueChange(
                                event.target.value,
                                "emotion"
                            )
                        }
                        value={thisDream.emotion}
                    />
                    <DreamEmotionSymbol
                        symbol={thisDream.emotion}
                        doesHaveLinks={isLinked}
                    />
                </div>
                <SelectDreamAttribute
                    dream={thisDream}
                    onChange={(newDreamAttributes) =>
                        this.HandleValueChange(newDreamAttributes, "attributes")
                    }
                />
                <div>
                    <FormTextArea
                        id="inputDreamNotes"
                        label="Notes"
                        width="83%"
                        value={thisDream.notes}
                        onChange={(value) =>
                            this.HandleValueChange(
                                value,
                                Nameof<IDream>("notes")
                            )
                        }
                        onClear={() =>
                            this.HandleValueChange("", Nameof<IDream>("notes"))
                        }
                    />
                </div>
                <Switch
                    label={"Today?"}
                    onChange={(value) =>
                        this.HandleValueChange(
                            value,
                            Nameof<IDream>("isresonating")
                        )
                    }
                    isChecked={thisDream.isresonating}
                />
            </div>
        );
    }
}

function RemovePatchTextLink({
    dreamName,
    onRemovePatchText,
}: {
    dreamName: string;
    onRemovePatchText: (newDreamName: string) => void;
}) {
    const dreamNameWithoutPatchText = GetDreamNameWithoutPatchText(dreamName);
    const hasPatchText = dreamNameWithoutPatchText !== dreamName;

    return (
        <Render id="removeDreamPatchText" when={hasPatchText}>
            <div style={{ marginTop: `5px`, marginBottom: `5px` }}>
                <IconWithText
                    iconSuffix={EGlyphicon.erase}
                    whenClicked={() =>
                        onRemovePatchText(dreamNameWithoutPatchText)
                    }
                    displayAsBlock={false}
                >
                    remove patch text
                </IconWithText>
            </div>
        </Render>
    );
}

export function GetDreamNameWithoutPatchText(dreamName: string) {
    const patchStart = dreamName.indexOf(`>`);

    if (patchStart === -1) {
        return dreamName;
    }

    const beforePatch = dreamName.slice(0, patchStart).trimEnd();
    const afterPatch = dreamName.slice(patchStart + 1);
    const hashTagStart = afterPatch.search(/(^|\s)#\S+/);

    if (hashTagStart === -1) {
        return beforePatch;
    }

    const hashTagTail = afterPatch.slice(hashTagStart).trimStart();
    return [beforePatch, hashTagTail].filter((part) => part !== ``).join(` `);
}

export const GetEditorTitle = (type: DreamType, dream: IDream) => {
    if (type === DreamType.New) {
        return `Begin a new dream`;
    }

    var shortenedDreamName = GetShortenedDreamName(dream);

    if (type === DreamType.Existing) {
        return `Edit "${shortenedDreamName}"`;
    }

    if (type === DreamType.Copied) {
        return `Copy from "${shortenedDreamName}"`;
    }
};

export const AddAction = async (willSave: boolean) => {
    const dream: Partial<IDream> = GetEditedDream();

    if (willSave) {
        delete dream.id;
        delete dream.lastmodifiedtime;
        await ClientDataProvider.AddRecordAsync(resourceNameDream, dream);
        await Data.Load.Single.ByStateTarget<IDreamsState>("dreamsdata");
        toastr.success("New dream added!");
    }
};

export const UpdateAction = async (willSave: boolean) => {
    const dream: IDream = GetEditedDream();

    if (willSave) {
        await dreamService.UpdateDreamAsync(dream, ClientDataProvider);
        await Data.Load.Single.ByStateTarget<IDreamsState>("dreamsdata");
    }
};

const DreamTimeText = ({ dreamTime }: { dreamTime: IDreamTime }) => (
    <span
        {...Style(`dreamTime`, {
            opacity: 0.5,
            fontWeight: `bold`,
            marginLeft: `5px`,
        })}
    >
        {dreamTime.fullName}
    </span>
);
